import React from 'react';
import {
  Store,
  Package,
  Receipt,
  FileText,
  Truck,
  Megaphone,
  CreditCard,
  ShoppingBag
} from 'lucide-react';
import './AuthSplitLayout.css';

/**
 * Shared two-pane layout for the login + signup pages.
 *  - Mobile (<1024px): only the form is visible, full bleed.
 *  - Desktop (>=1024px): brand pane on the LEFT, form on the RIGHT.
 *
 * The brand pane renders an orbit of 8 product icons rotating slowly around a
 * central headline. Icons stay upright (counter-rotated) so the motion reads
 * as "things in flight" rather than a Ferris wheel. Disabled under
 * prefers-reduced-motion (CSS handles it).
 *
 * Brand pane is decorative and aria-hidden, so the page's own h1 (in `children`)
 * remains the canonical heading for assistive tech on every viewport.
 */

// Order matters — these are rendered at angles 0°, 45°, 90°, ..., 315°.
// Each entry gets a card in the center of the orbit; only one card is visible
// at a time, paired in time with its icon orbiting through.
const ORBIT_ICONS = [
  { Icon: Store,       label: 'Storefront', description: 'Your branded online store' },
  { Icon: Package,     label: 'Inventory',  description: 'Real-time stock across locations' },
  { Icon: Receipt,     label: 'Receipts',   description: 'Branded receipts in one tap' },
  { Icon: FileText,    label: 'Invoices',   description: 'Invoices, reminders, and payments' },
  { Icon: Truck,       label: 'Delivery',   description: 'Connect to delivery partners' },
  { Icon: Megaphone,   label: 'Campaigns',  description: 'Email and SMS campaigns' },
  { Icon: CreditCard,  label: 'POS',        description: 'Sell in person, anywhere' },
  { Icon: ShoppingBag, label: 'Products',   description: 'Full catalog with variants' }
];

const AuthSplitLayout = ({ children }) => {
  return (
    <div className="auth-layout">
      <div className="auth-layout-form">{children}</div>
      <aside className="auth-layout-brand" aria-hidden="true">
        <img src="/logo192.png" alt="" className="auth-brand-logo" />

        <div className="auth-brand-headline">
          <h2 className="auth-brand-title">Sell. Grow. Scale.</h2>
          <p className="auth-brand-tagline">Set up your store in minutes. No credit card required.</p>
        </div>

        <div className="auth-brand-orbit">
          {/* Subtle inner ring to anchor the orbit visually */}
          <div className="auth-brand-orbit-ring" />

          {/* Center card — one visible at a time, fades through each feature
              in sync with the icon orbiting past. */}
          {ORBIT_ICONS.map(({ Icon, label, description }, i) => (
            <div
              key={`card-${label}`}
              className="auth-brand-orbit-card"
              style={{
                '--i': i,
                '--total': ORBIT_ICONS.length
              }}
            >
              <div className="auth-brand-orbit-card-icon">
                <Icon size={28} strokeWidth={1.75} />
              </div>
              <h3 className="auth-brand-orbit-card-title">{label}</h3>
              <p className="auth-brand-orbit-card-description">{description}</p>
            </div>
          ))}

          {/* Orbiting icons — decorative, no labels (storytelling lives in the card) */}
          {ORBIT_ICONS.map(({ Icon, label }, i) => (
            <div
              key={`slot-${label}`}
              className="auth-brand-orbit-slot"
              style={{
                '--i': i,
                '--total': ORBIT_ICONS.length
              }}
            >
              <div className="auth-brand-orbit-icon" title={label}>
                <Icon size={26} strokeWidth={1.75} />
              </div>
            </div>
          ))}
        </div>
      </aside>
    </div>
  );
};

export default AuthSplitLayout;
